import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PAGE_VIEW_ROLES } from '@onboarding-dashboard/shared';
import { useAuth } from '../../contexts/AuthContext.js';
import { notificationService } from '../../services/notification.js';
import navBarTemplate from './nav-bar.html?raw';
import navLogoUrl from '../../assets/eXeX_Title_White_no_name.png';
import './nav-bar.css';

export function NavBar(): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasRole, logout } = useAuth();

  const updateBadge = useCallback(async () => {
    try {
      const result = await notificationService.unreadCount();
      if (result.success && result.data) {
        const container = containerRef.current;
        if (!container) return;
        const badge = container.querySelector<HTMLSpanElement>('#nav-notification-badge');
        if (badge) {
          if (result.data.count > 0) {
            badge.textContent = result.data.count > 99 ? '99+' : String(result.data.count);
            badge.style.display = '';
          } else {
            badge.style.display = 'none';
          }
        }
      }
    } catch {
      // ignore badge fetch errors
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Set logo src
    const logoEl = container.querySelector<HTMLImageElement>('#nav-logo');
    if (logoEl) logoEl.src = navLogoUrl;

    // Set user name
    const nameEl = container.querySelector<HTMLSpanElement>('#nav-user-name');
    if (nameEl && user) {
      nameEl.textContent = `${user.firstName} ${user.lastName}`;
    }

    // Show/hide links based on role
    const isExternal = user?.role === 'external';
    const canViewHiring =
      hasRole('admin') || hasRole('hr') || hasRole('manager') || hasRole('employee');
    const canViewMembers = hasRole(PAGE_VIEW_ROLES.members);
    const canViewMC = hasRole(PAGE_VIEW_ROLES.missionControl);

    const pipelineLink = container.querySelector<HTMLElement>('#nav-pipeline-link');
    const rolesLink = container.querySelector<HTMLElement>('#nav-roles-link');
    const membersLink = container.querySelector<HTMLElement>('#nav-members-link');
    const mcLink = container.querySelector<HTMLElement>('#nav-mc-link');
    const insightsLink = container.querySelector<HTMLElement>('#nav-insights-link');

    if (pipelineLink) pipelineLink.style.display = canViewHiring ? '' : 'none';
    if (rolesLink) rolesLink.style.display = canViewHiring ? '' : 'none';
    if (membersLink) membersLink.style.display = canViewMembers ? '' : 'none';
    if (mcLink) mcLink.style.display = canViewMC ? '' : 'none';
    const canViewInsights = hasRole(PAGE_VIEW_ROLES.insights);
    if (insightsLink) insightsLink.style.display = canViewInsights ? '' : 'none';
    const canViewCC = hasRole(PAGE_VIEW_ROLES.commandCenter);
    const ccLink = container.querySelector<HTMLElement>('#nav-cc-link');
    if (ccLink) ccLink.style.display = canViewCC ? '' : 'none';
    const canViewCanopus = hasRole(PAGE_VIEW_ROLES.canopusCommand);
    const canopusLink = container.querySelector<HTMLElement>('#nav-canopus-link');
    if (canopusLink) canopusLink.style.display = canViewCanopus ? '' : 'none';
    const canViewBrandBook = hasRole(PAGE_VIEW_ROLES.brandBook);
    const brandBookLink = container.querySelector<HTMLElement>('#nav-brand-book-link');
    if (brandBookLink) brandBookLink.style.display = canViewBrandBook ? '' : 'none';

    // Highlight active link
    const linkMap: { selector: string; path: string }[] = [
      { selector: '#nav-pipeline-link', path: '/pipeline' },
      { selector: '#nav-roles-link', path: '/roles' },
      { selector: '#nav-members-link', path: '/members' },
      { selector: '#nav-mc-link', path: '/mission-control' },
      { selector: '#nav-insights-link', path: '/insights' },
      { selector: '#nav-cc-link', path: '/command-center' },
      { selector: '#nav-canopus-link', path: '/canopus-command' },
      { selector: '#nav-brand-book-link', path: '/brand-book' },
    ];
    for (const { selector, path } of linkMap) {
      const el = container.querySelector<HTMLElement>(selector);
      if (el) {
        el.classList.toggle(
          'active',
          location.pathname === path || location.pathname.startsWith(path + '/')
        );
      }
    }

    // Wire up click handlers
    const links: { selector: string; path: string }[] = [
      { selector: '#nav-home-link', path: '/' },
      { selector: '#nav-pipeline-link', path: '/pipeline' },
      { selector: '#nav-roles-link', path: '/roles' },
      { selector: '#nav-members-link', path: '/members' },
      { selector: '#nav-mc-link', path: '/mission-control' },
      { selector: '#nav-insights-link', path: '/insights' },
      { selector: '#nav-cc-link', path: '/command-center' },
      { selector: '#nav-canopus-link', path: '/canopus-command' },
      { selector: '#nav-user-name', path: '/profile' },
    ];

    const cleanups: (() => void)[] = [];
    for (const { selector, path } of links) {
      const el = container.querySelector<HTMLElement>(selector);
      if (el) {
        const handler = (e: Event): void => {
          e.preventDefault();
          navigate(path);
        };
        el.addEventListener('click', handler);
        cleanups.push(() => el.removeEventListener('click', handler));
      }
    }

    // Hide home link + notifications for external-only users
    const homeLink = container.querySelector<HTMLElement>('#nav-home-link');
    if (homeLink) homeLink.style.display = isExternal ? 'none' : '';

    // Hide the notifications bell — page removed
    const notifBtn = container.querySelector<HTMLButtonElement>('#nav-notifications-btn');
    if (notifBtn) notifBtn.style.display = 'none';

    // Logout button
    const logoutBtn = container.querySelector<HTMLButtonElement>('#nav-logout-btn');
    if (logoutBtn) {
      const handler = (): void => {
        logout();
      };
      logoutBtn.addEventListener('click', handler);
      cleanups.push(() => logoutBtn.removeEventListener('click', handler));
    }

    // Fetch unread count
    void updateBadge();

    // Poll every 60 seconds
    const interval = setInterval(() => {
      void updateBadge();
    }, 60000);
    cleanups.push(() => clearInterval(interval));

    return () => {
      cleanups.forEach((fn) => fn());
    };
  }, [user, hasRole, navigate, location.pathname, logout, updateBadge]);

  return <div ref={containerRef} dangerouslySetInnerHTML={{ __html: navBarTemplate }} />;
}
